import { motion, useReducedMotion } from "framer-motion";
import { useLang } from "@/context/LangContext";
import { Eyebrow, EASE } from "@/components/Reveal";

const draw = (reduce) =>
  reduce
    ? {}
    : {
        initial: { pathLength: 0, opacity: 0 },
        whileInView: { pathLength: 1, opacity: 1 },
        viewport: { once: false, amount: 0.6 },
        transition: { duration: 1.4, ease: EASE },
      };

const fade = (reduce, delay = 0) =>
  reduce
    ? {}
    : {
        initial: { opacity: 0, y: 24 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, amount: 0.4 },
        transition: { duration: 0.9, delay, ease: EASE },
      };

function DiagramClarity({ reduce }) {
  return (
    <svg viewBox="0 0 160 160" className="h-36 w-36 md:h-44 md:w-44" aria-hidden="true">
      <motion.circle cx="80" cy="80" r="58" fill="none" stroke="#0047FF" strokeWidth="1.2" {...draw(reduce)} />
      <motion.line x1="80" y1="10" x2="80" y2="150" stroke="#111110" strokeOpacity="0.3" strokeWidth="1" {...draw(reduce)} transition={{ duration: 1, delay: 0.3, ease: EASE }} />
      <motion.line x1="10" y1="80" x2="150" y2="80" stroke="#111110" strokeOpacity="0.3" strokeWidth="1" {...draw(reduce)} transition={{ duration: 1, delay: 0.45, ease: EASE }} />
      <motion.rect x="66" y="66" width="28" height="28" fill="none" stroke="#0047FF" strokeWidth="1.2" {...draw(reduce)} transition={{ duration: 1, delay: 0.6, ease: EASE }} />
    </svg>
  );
}

function DiagramPrivacy({ reduce }) {
  return (
    <svg viewBox="0 0 160 160" className="h-36 w-36 md:h-44 md:w-44" aria-hidden="true">
      <motion.path
        d="M80 18 L128 38 V84 C128 122 106 142 80 154 C54 142 32 122 32 84 V38 Z"
        fill="none" stroke="#0047FF" strokeWidth="1.2" {...draw(reduce)}
      />
      <motion.circle cx="80" cy="76" r="12" fill="none" stroke="#111110" strokeOpacity="0.45" strokeWidth="1.2" {...draw(reduce)} transition={{ duration: 0.9, delay: 0.5, ease: EASE }} />
      <motion.line x1="80" y1="84" x2="80" y2="106" stroke="#111110" strokeOpacity="0.45" strokeWidth="1.2" {...draw(reduce)} transition={{ duration: 0.7, delay: 0.8, ease: EASE }} />
    </svg>
  );
}

function DiagramContinuity({ reduce }) {
  return (
    <svg viewBox="0 0 160 160" className="h-36 w-36 md:h-44 md:w-44" aria-hidden="true">
      <motion.path
        d="M18 138 H58 V98 H98 V58 H142"
        fill="none" stroke="#0047FF" strokeWidth="1.2" {...draw(reduce)}
      />
      {[[18, 138], [58, 98], [98, 58], [142, 58]].map(([cx, cy], i) => (
        <motion.circle
          key={i} cx={cx} cy={cy} r="3.5" fill="#F9F9F7" stroke="#111110" strokeOpacity="0.5" strokeWidth="1.2"
          {...(reduce
            ? {}
            : {
                initial: { scale: 0, opacity: 0 },
                whileInView: { scale: 1, opacity: 1 },
                viewport: { once: false, amount: 0.6 },
                transition: { duration: 0.5, delay: 0.4 + i * 0.25, ease: EASE },
              })}
        />
      ))}
      <motion.line x1="142" y1="58" x2="142" y2="22" stroke="#111110" strokeOpacity="0.25" strokeWidth="1" strokeDasharray="3 4" {...draw(reduce)} transition={{ duration: 0.8, delay: 1.2, ease: EASE }} />
    </svg>
  );
}

export default function Principles() {
  const { t } = useLang();
  const reduce = useReducedMotion();
  const chapters = t("standard.chapters");
  const diagrams = [DiagramClarity, DiagramPrivacy, DiagramContinuity];

  return (
    <section id="standard" data-testid="vlr-standard" className="border-t border-ink/10 bg-white">
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 md:grid-cols-12">
        <div className="md:col-span-5">
          <div className="sticky top-[72px] px-5 py-16 md:px-10 md:py-24">
            <Eyebrow>{t("standard.eyebrow")}</Eyebrow>
            <h2 className="mt-6 max-w-sm font-serif text-4xl font-medium leading-[1.05] tracking-tight text-ink md:text-5xl">
              {t("standard.title")}
            </h2>
            <svg viewBox="0 0 120 24" className="mt-10 h-6 w-28" aria-hidden="true">
              <line x1="0" y1="12" x2="120" y2="12" stroke="#111110" strokeOpacity="0.15" strokeWidth="1" />
              <line x1="0" y1="12" x2="34" y2="12" stroke="#0047FF" strokeWidth="1.5" />
              <line x1="58" y1="6" x2="58" y2="18" stroke="#0047FF" strokeWidth="1.5" />
            </svg>
          </div>
        </div>

        <div className="md:col-span-7 md:border-l md:border-ink/10">
          {chapters.map((ch, i) => {
            const Diagram = diagrams[i];
            return (
              <motion.article
                key={ch.n}
                data-testid={`standard-chapter-${ch.n}`}
                {...fade(reduce)}
                className="flex min-h-[62vh] flex-col justify-center border-t border-ink/10 px-5 py-16 md:px-14 md:py-24"
              >
                <div className="flex items-start justify-between gap-8">
                  <div>
                    <span className="font-sans text-6xl font-light leading-none text-cobalt md:text-7xl">{ch.n}</span>
                    <h3 className="mt-8 font-serif text-3xl font-medium tracking-tight text-ink md:text-4xl">
                      {ch.t}
                    </h3>
                    <p className="mt-5 max-w-md text-base leading-relaxed text-ink/60">{ch.d}</p>
                  </div>
                  <Diagram reduce={reduce} />
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
